import { useState, useEffect } from 'react';

const getIsMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
};

const getIsPinned = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('left-menu-pinned') === 'true';
};

export const useMenuState = () => {
    const [isMobile, setIsMobile] = useState(getIsMobile);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(getIsPinned);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(getIsMobile());
            // Close mobile menu when resizing to desktop
            if (!getIsMobile()) {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleMenu = () => {
        if (isMobile) {
            const newMobileState = !mobileMenuOpen;
            setMobileMenuOpen(newMobileState);
            // Dispatch event for mobile menu state changes
            window.dispatchEvent(new CustomEvent('mobileMenuStateChanged', { detail: { isOpen: newMobileState } }));
        }
    };

    const closeMobileMenu = () => {
        if (isMobile) {
            setMobileMenuOpen(false);
            window.dispatchEvent(new CustomEvent('mobileMenuStateChanged', { detail: { isOpen: false } }));
        }
    };

    const togglePinned = () => {
        const nextPinned = !isPinned;
        setIsPinned(nextPinned);
        localStorage.setItem('left-menu-pinned', String(nextPinned));
        window.dispatchEvent(new CustomEvent('menuPinnedStateChanged', { detail: { isPinned: nextPinned } }));
    };

    // Listen for state changes
    useEffect(() => {
        const handleMobileMenuStateChange = (event: CustomEvent<{ isOpen: boolean }>) => {
            if (isMobile) {
                setMobileMenuOpen(event.detail.isOpen);
            }
        };

        const handlePinnedStateChange = (event: CustomEvent<{ isPinned: boolean }>) => {
            setIsPinned(event.detail.isPinned);
        };

        window.addEventListener('mobileMenuStateChanged', handleMobileMenuStateChange as EventListener);
        window.addEventListener('menuPinnedStateChanged', handlePinnedStateChange as EventListener);

        return () => {
            window.removeEventListener('mobileMenuStateChanged', handleMobileMenuStateChange as EventListener);
            window.removeEventListener('menuPinnedStateChanged', handlePinnedStateChange as EventListener);
        };
    }, [isMobile]);

    return {
        isOpen: mobileMenuOpen,
        toggleMenu,
        closeMobileMenu,
        isMobile,
        isPinned,
        togglePinned,
        menuWidth: isMobile ? 0 : (isPinned ? 250 : 70),
    };
};
